import { ColumnItemElement } from '@typedream/components';
import {
  ColumnElement,
  ColumnItemElement as ColumnItemElementType,
  DreamValue,
} from '@typedream/core';
import {
  findNodePath,
  getNodeEntry,
  getParentNode,
  isEditorNormalizing,
  isFirstChild,
  isLastChild,
  normalizeEditor,
  PlateRenderElementProps,
  setElements,
  TNodeEntry,
  toDOMNode,
} from '@udecode/plate-core';
import { pick } from 'lodash';
import { useRef } from 'react';
import { Editor, Path } from 'slate';
import { MERGING } from 'slate-history';
import { useDreamEditorStore } from '../../../plugins/store';
import { ResizeHandle } from './ColumnItemResizeHandle';

export function ColumnItemElementEditor(
  props: PlateRenderElementProps<DreamValue, ColumnItemElementType>,
) {
  const { editor, element, children } = props;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const path = findNodePath(editor, element)!;

  const parentEntry = useDreamEditorStore(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    () => getParentNode<ColumnElement>(editor, path)!,
  );
  const [parent] = parentEntry;
  const parentActualWidth = useRef<number>(1);

  const isFirstColumnItem = isFirstChild(path);
  const isLastColumnItem = isLastChild(parentEntry, path);

  const isNormalizing = useRef(true);
  const merging = useRef<boolean | undefined>(undefined);

  const handleResizeStart = () => {
    merging.current = MERGING.get(editor as Editor);
    const parentDomNode = toDOMNode(editor, parent);

    if (!parentDomNode) return;

    parentActualWidth.current = parentDomNode.getBoundingClientRect().width || 1;

    // Turn off normalization to avoid excessive computation of
    // normalization on every pixel of dragging
    isNormalizing.current = isEditorNormalizing(editor);
    Editor.setNormalizing(editor as Editor, false);
  };

  const handleResize = (
    deltaX: number,
    entryBefore?: TNodeEntry<ColumnItemElementType> | null,
    entryAfter?: TNodeEntry<ColumnItemElementType> | null,
  ) => {
    const percentDelta = deltaX / parentActualWidth.current;

    if (entryBefore && parent.align !== 'align_right') {
      const [node, at] = entryBefore;
      setElements(editor, { width: node.width + percentDelta }, { at });
    }
    if (entryAfter && parent.align !== 'align_left') {
      const [node, at] = entryAfter;
      setElements(editor, { width: node.width - percentDelta }, { at });
    }
    MERGING.set(editor as Editor, true);
  };

  const handleResizeStop = () => {
    MERGING.set(editor as Editor, merging.current);
    // Restore normalization
    Editor.setNormalizing(editor as Editor, isNormalizing.current);
    normalizeEditor(editor);
  };

  return (
    <ColumnItemElement {...pick(props, ['element', 'className', 'attributes'])}>
      {children}

      {
        // show resize handle at the start of columns when
        // align is set to center or right
        isFirstColumnItem
          && parent.align
          && ['align_center', 'align_right'].includes(parent.align) && (
            <ResizeHandle
              placement="left"
              onResizeStart={handleResizeStart}
              onResize={(deltaX) => {
                handleResize(deltaX, null, [element, path]);
              }}
              onResizeStop={handleResizeStop}
            />
        )
      }

      {
        // show resize handle at between and the end of columns except when
        // align is set to dynamic or right
        (!isLastColumnItem
          || (parent?.align
            && !['column_dynamic', 'align_right'].includes(parent.align))) && (
            <ResizeHandle
              placement="right"
              onResizeStart={handleResizeStart}
              onResize={(deltaX) => {
                handleResize(
                  deltaX,
                  [element, path],
                  isLastColumnItem ? null : getNodeEntry(editor, Path.next(path)),
                );
              }}
              onResizeStop={handleResizeStop}
            />
        )
      }
    </ColumnItemElement>
  );
}
